import { useState, useCallback, useMemo } from 'react';
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import type { TestCase, TestCaseStatus } from '@/lib/types';
import { Button } from '@/components/ui/button';

// ─── Types ────────────────────────────────────────────────────────────────────

type RunState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'polling'; replayId: string; agentUrl: string }
  | { kind: 'done'; status: 'pass' | 'fail'; error?: string | undefined };

interface TestCaseState {
  tc: TestCase;
  run: RunState;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatusBadge({
  status,
}: {
  status: TestCaseStatus | 'running';
}): import('react/jsx-runtime').JSX.Element {
  if (status === 'running') {
    return (
      <span className="flex items-center gap-1 text-[11px] font-mono text-blue-400">
        <Loader2 className="h-3 w-3 animate-spin" /> running
      </span>
    );
  }
  if (status === 'pass') {
    return (
      <span className="flex items-center gap-1 text-[11px] font-mono text-emerald-400">
        <CheckCircle2 className="h-3 w-3" /> pass
      </span>
    );
  }
  if (status === 'fail') {
    return (
      <span className="flex items-center gap-1 text-[11px] font-mono text-red-400">
        <XCircle className="h-3 w-3" /> fail
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground/50">
      <Clock className="h-3 w-3" /> pending
    </span>
  );
}

function timeAgo(iso: string | null): string {
  if (iso == null) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function groupByEntry(cases: TestCaseState[]): Map<string, TestCaseState[]> {
  const map = new Map<string, TestCaseState[]>();
  for (const cs of cases) {
    const key = cs.tc.entry ?? '(unknown function)';
    const arr = map.get(key) ?? [];
    arr.push(cs);
    map.set(key, arr);
  }
  return map;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export interface TestRunnerProps {
  projectId: string;
  agentUrl: string | null;
  initialTestCases: TestCase[];
}

export function TestRunner({
  projectId,
  agentUrl,
  initialTestCases,
}: TestRunnerProps): import('react/jsx-runtime').JSX.Element {
  const [states, setStates] = useState<TestCaseState[]>(() =>
    initialTestCases.map((tc) => ({ tc, run: { kind: 'idle' } })),
  );
  const [runningAll, setRunningAll] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const groups = useMemo((): Map<string, TestCaseState[]> => groupByEntry(states), [states]);

  // Update run state for a single test case
  const setRunState = useCallback(
    (tcId: string, run: RunState, newStatus?: TestCaseStatus): void => {
      setStates((prev): TestCaseState[] =>
        prev.map(
          (s): TestCaseState =>
            s.tc.id !== tcId
              ? s
              : {
                  tc: newStatus
                    ? { ...s.tc, last_status: newStatus, last_run_at: new Date().toISOString() }
                    : s.tc,
                  run,
                },
        ),
      );
    },
    [],
  );

  // Run a single test case
  async function runOne(tcId: string): Promise<void> {
    setRunState(tcId, { kind: 'running' });
    try {
      const res = await fetch(`/api/projects/${projectId}/test-cases/${tcId}/run`, {
        method: 'POST',
      });
      const json = (await res.json()) as {
        ok: boolean;
        async?: boolean;
        replayId?: string;
        agentUrl?: string;
        status?: 'pass' | 'fail';
        error?: string;
      };

      if (!json.ok) {
        setRunState(tcId, { kind: 'done', status: 'fail', error: json.error }, 'fail');
        return;
      }

      if ((json.async ?? false) && json.replayId != null && json.agentUrl != null) {
        // Poll until done
        setRunState(tcId, { kind: 'polling', replayId: json.replayId, agentUrl: json.agentUrl });
        await pollUntilDone(tcId, json.replayId, json.agentUrl, projectId);
      } else if (json.status) {
        setRunState(tcId, { kind: 'done', status: json.status }, json.status);
      } else {
        setRunState(tcId, { kind: 'done', status: 'pass' }, 'pass');
      }
    } catch (err) {
      setRunState(tcId, { kind: 'done', status: 'fail', error: String(err) }, 'fail');
    }
  }

  // Poll agent /poll/:replayId and then PATCH test case status
  async function pollUntilDone(
    tcId: string,
    replayId: string,
    agentUrl: string,
    projectId: string,
  ): Promise<void> {
    const MAX_POLLS = 120;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, 800));
      try {
        const pollRes = await fetch(`${agentUrl}/poll/${replayId}`);
        if (!pollRes.ok) continue;
        const data = (await pollRes.json()) as {
          status: 'running' | 'done' | 'error';
          error?: string;
          result?: unknown;
        };

        if (data.status === 'done' || data.status === 'error') {
          // Get stored test case output to compare
          const tcState = states.find((s): boolean => s.tc.id === tcId);
          const expectedOutput = tcState?.tc.output_snapshot;

          let status: 'pass' | 'fail';
          if (data.status === 'error') {
            status = 'fail';
          } else if (expectedOutput !== null && expectedOutput !== undefined) {
            status =
              stableStringify(data.result) === stableStringify(expectedOutput) ? 'pass' : 'fail';
          } else {
            status = 'pass'; // no expected output — replay completing = pass
          }

          // Update DB
          await fetch(`/api/projects/${projectId}/test-cases/${tcId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ last_status: status }),
          });

          setRunState(tcId, { kind: 'done', status, error: data.error }, status);
          return;
        }
      } catch {
        // transient — keep polling
      }
    }
    // Timed out
    setRunState(
      tcId,
      { kind: 'done', status: 'fail', error: 'Timed out waiting for replay' },
      'fail',
    );
  }

  // Run all test cases sequentially
  async function runAll(): Promise<void> {
    if (runningAll || agentUrl == null) return;
    setRunningAll(true);
    for (const s of states) {
      if (s.run.kind === 'running' || s.run.kind === 'polling') continue;
      await runOne(s.tc.id);
    }
    setRunningAll(false);
  }

  // Delete a test case
  async function deleteTestCase(tcId: string): Promise<void> {
    setDeletingId(tcId);
    try {
      await fetch(`/api/projects/${projectId}/test-cases/${tcId}`, { method: 'DELETE' });
      setStates((prev): TestCaseState[] => prev.filter((s): boolean => s.tc.id !== tcId));
    } finally {
      setDeletingId(null);
    }
  }

  // Rename a test case
  async function renameTestCase(tcId: string, newName: string): Promise<void> {
    if (!newName.trim()) return;
    await fetch(`/api/projects/${projectId}/test-cases/${tcId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    });
    setStates((prev): TestCaseState[] =>
      prev.map(
        (s): TestCaseState => (s.tc.id === tcId ? { ...s, tc: { ...s.tc, name: newName } } : s),
      ),
    );
  }

  const passing = states.filter((s): boolean => s.tc.last_status === 'pass').length;
  const failing = states.filter((s): boolean => s.tc.last_status === 'fail').length;
  const pending = states.filter((s): boolean => s.tc.last_status === 'pending').length;
  const hasAny = states.length > 0;
  const isAnyRunning = states.some(
    (s): boolean => s.run.kind === 'running' || s.run.kind === 'polling',
  );

  if (!hasAny) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <div className="p-4 rounded-full bg-white/[0.04] border border-white/[0.08]">
          <FlaskConical className="h-8 w-8 text-muted-foreground/40" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground/80">No test cases yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs">
            Open any execution in the explorer and click <strong>Pin as test</strong> to save it
            here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className="text-emerald-400">{passing} pass</span>
          <span className="text-white/20">·</span>
          <span className="text-red-400">{failing} fail</span>
          <span className="text-white/20">·</span>
          <span className="text-muted-foreground/50">{pending} pending</span>
          <span className="text-white/20">·</span>
          <span className="text-muted-foreground/40">{states.length} total</span>
        </div>
        <span className="flex-1" />
        {agentUrl == null && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded-md px-2.5 py-1">
            <AlertTriangle className="h-3 w-3" />
            No agent URL — configure one in project settings to run tests
          </div>
        )}
        <Button
          size="sm"
          disabled={agentUrl == null || isAnyRunning || runningAll}
          onClick={runAll}
          className="gap-1.5"
        >
          {runningAll || isAnyRunning ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5 fill-current" />
          )}
          Run all
        </Button>
      </div>

      {/* Groups */}
      {Array.from(groups.entries()).map(
        ([entry, groupStates]): import('react/jsx-runtime').JSX.Element => {
          const isCollapsed_ = collapsed.has(entry);
          const groupPass = groupStates.filter((s): boolean => s.tc.last_status === 'pass').length;
          const groupFail = groupStates.filter((s): boolean => s.tc.last_status === 'fail').length;

          return (
            <div
              key={entry}
              className="rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden"
            >
              {/* Group header */}
              <button
                onClick={(): void =>
                  setCollapsed((prev): Set<string> => {
                    const next = new Set(prev);
                    if (next.has(entry)) next.delete(entry);
                    else next.add(entry);
                    return next;
                  })
                }
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors"
              >
                {isCollapsed_ ? (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                )}
                <FlaskConical className="h-3.5 w-3.5 text-purple-400/70 shrink-0" />
                <span className="font-mono text-sm text-foreground/80 truncate">{entry}</span>
                <span className="ml-auto flex items-center gap-2 text-[11px] font-mono shrink-0">
                  {groupFail > 0 && <span className="text-red-400">{groupFail} fail</span>}
                  {groupPass > 0 && <span className="text-emerald-400">{groupPass} pass</span>}
                  <span className="text-muted-foreground/40">{groupStates.length}</span>
                </span>
              </button>

              {/* Test cases */}
              {!isCollapsed_ && (
                <div className="divide-y divide-white/[0.04]">
                  {groupStates.map(({ tc, run }): import('react/jsx-runtime').JSX.Element => {
                    const isRunning = run.kind === 'running' || run.kind === 'polling';
                    const displayStatus: TestCaseStatus | 'running' = isRunning
                      ? 'running'
                      : tc.last_status;

                    return (
                      <div
                        key={tc.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors group"
                      >
                        {/* Status */}
                        <div className="w-20 shrink-0">
                          <StatusBadge status={displayStatus} />
                        </div>

                        {/* Name (editable) */}
                        <div className="flex-1 min-w-0">
                          <InlineEdit
                            value={tc.name}
                            onSave={(v): Promise<void> => renameTestCase(tc.id, v)}
                          />
                          {run.kind === 'done' && run.error != null && (
                            <p
                              className="text-[11px] text-red-400/80 mt-0.5 truncate"
                              title={run.error}
                            >
                              {run.error}
                            </p>
                          )}
                          {tc.last_run_at != null && !isRunning && (
                            <p className="text-[10px] text-muted-foreground/30 mt-0.5">
                              ran {timeAgo(tc.last_run_at)}
                            </p>
                          )}
                          {!tc.input_snapshot && (
                            <p className="text-[10px] text-amber-400/50 mt-0.5">
                              no snapshot — re-pin to capture inputs
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <Link
                            to={`/projects/${projectId}/executions/${tc.execution_id}`}
                            title="View original execution"
                            className="p-1.5 rounded hover:bg-white/[0.08] text-muted-foreground/50 hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={(): Promise<void> => deleteTestCase(tc.id)}
                            disabled={deletingId === tc.id}
                            title="Delete test case"
                            className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground/50 hover:text-red-400 transition-colors disabled:opacity-50"
                          >
                            {deletingId === tc.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            onClick={(): Promise<void> => runOne(tc.id)}
                            disabled={isRunning || agentUrl == null}
                            title="Run this test"
                            className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                          >
                            {isRunning ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3 fill-current" />
                            )}
                            Run
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        },
      )}
    </div>
  );
}

// ─── Inline Edit ─────────────────────────────────────────────────────────────

function InlineEdit({
  value,
  onSave,
}: {
  value: string;
  onSave: (v: string) => void;
}): import('react/jsx-runtime').JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <form
        onSubmit={(e): void => {
          e.preventDefault();
          onSave(draft);
          setEditing(false);
        }}
        className="flex items-center gap-1.5"
      >
        <input
          autoFocus
          value={draft}
          onChange={(e): void => setDraft(e.target.value)}
          onBlur={(): void => {
            onSave(draft);
            setEditing(false);
          }}
          onKeyDown={(e): void => {
            if (e.key === 'Escape') {
              setDraft(value);
              setEditing(false);
            }
          }}
          className="flex-1 bg-transparent border-b border-white/20 text-sm text-foreground/80 focus:outline-none focus:border-blue-500/60 font-mono"
        />
      </form>
    );
  }

  return (
    <button
      onClick={(): void => {
        setDraft(value);
        setEditing(true);
      }}
      className="text-sm text-foreground/80 font-mono text-left truncate hover:text-foreground transition-colors"
      title={value}
    >
      {value}
    </button>
  );
}

// Stable JSON stringify (for output comparison on client side)
function stableStringify(val: unknown): string {
  return JSON.stringify(val, (_, v): unknown => {
    if (v === undefined) return null;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).sort(([a], [b]): number => a.localeCompare(b)),
      );
    }
    return v as unknown;
  });
}
